import { useCallback, useMemo } from 'react';
import { HealthPlanStatuses } from '@common-fe/common-fe';
import _ from 'lodash';

import ROUTES from '@/common/routes';
import {
  GlobalLoaderMessages,
  useGlobalLoaderStore,
} from '@/components/elements/FullLoader/store/useGlobalLoader.store';
import { SnackbarState } from '@/modules/core/core.types';
import { useSnackbar } from '@/modules/core/hooks';
import { FeatureFlags } from '@/modules/featureFlags/featureFlags.constants';
import { useFeatureFlag } from '@/modules/featureFlags/hooks/useFeatureFlag';
import { HealthPlansType } from '@/modules/HealthPlan/HealthPlan.types';
import { useSubmitForm, useUpdateHealthPlan } from '@/modules/HealthPlan/HealthPlanSetup/hooks';

import { useHealthPlanNameStore } from '../../BasicProperties/HealthPlanName/stores';
import { useStore as useMatrixStore } from '../../ComplexConfiguration/EnrollmentMatrix/stores/useEnrollmentMatrix.store';
import { useProcessingRuleTiersStore } from '../../ComplexConfiguration/EnrollmentMatrix/stores/useProcessingRuleTiersStore';
import {
  useUpdatePayoutRulesQuery,
  useUpdateProcessingRuleTiersQuery,
} from '../../ComplexConfiguration/EnrollmentMatrix/Tiers/queries';
import { ProcessingRuleTierPayoutRuleType } from '../../ComplexConfiguration/EnrollmentMatrix/Tiers/queries/processingRuleTiers.types';
import { useEditModeStore, useSkipConfirmMode } from '../../stores';
import { usePlanErrorsStore } from '../stores';
import useModalType from '../stores/useModalType.store';

import useParseErrors, { ERROR_MESSAGES } from './useParseErrors';

const SNACKBAR_EXIT_TEXT = 'Close health plan config';
const PAYOUT_RULES_ERROR_TEXT = 'Failed to update payout rules. Please try again.';

const ERRORS = {
  UNAUTHORIZED: 401,
  BAD_REQUEST: 400,
  CONFLICT: 409,
  SERVER_ERROR: 500,
};
export const useHandleHealthPlanSetUp = (isTemplateMode?: boolean) => {
  const {
    setSaveErrorModal,
    setSubmitErrorModal,
    setUpdateErrorModal,
    setActivateErrorModal,
    setActivateModal,
    setIsEmptySaveModal,
    setIsEmptySubmitModal,
    setIsPendingOrganizationModal,
  } = useModalType();

  const setSkipConfirmMode = useSkipConfirmMode((state) => state.setSkipConfirm);

  const errorMessages = usePlanErrorsStore((state) => state.saveErrors);
  const successMessages = usePlanErrorsStore((state) => state.saveSuccess);
  const pendingMessages = usePlanErrorsStore((state) => state.savePendigns);

  const activateErrorMessages = usePlanErrorsStore((state) => state.activeErrors);
  const activateSuccessMessages = usePlanErrorsStore((state) => state.activeSuccess);
  const activatePedingMessages = usePlanErrorsStore((state) => state.activePendigns);

  const planName = useHealthPlanNameStore((state) => state.state.healthPlanName);
  const setEditMode = useEditModeStore((state) => state.setEditMode);
  const { setLoader, removeLoading } = useGlobalLoaderStore();
  const { handleAddPermanentSnackbar } = useSnackbar();
  const { setActivateValidate, setSaveValidate } = useSubmitForm();
  const handleParseErors = useParseErrors();
  const storeTiers = useProcessingRuleTiersStore((state) => state.tiers);
  const processingRuleName = useMatrixStore((state) => state.state.processingRuleName);
  const linkedPlanIds = useHealthPlanNameStore((state) => state.state.plans);
  const isPurseLockUpdateEnabled = useFeatureFlag(FeatureFlags.PURSE_LOCK_HP_UPDATE);
  const { mutateAsync: updatePayoutRules } = useUpdatePayoutRulesQuery();
  const { mutateAsync: updateProcessingRuleTiers } = useUpdateProcessingRuleTiersQuery();
  const hasLockedTierWithoutPlans = useMemo(() => {
    if (!isPurseLockUpdateEnabled || isTemplateMode || !processingRuleName || !storeTiers.length) {
      return false;
    }

    return storeTiers.some((tier) => {
      if (!tier.isLockPurse) return false;
      const selectedPlanIds = (tier.planIds || []).filter((planId) => linkedPlanIds?.includes(planId));
      return !selectedPlanIds.length;
    });
  }, [isPurseLockUpdateEnabled, isTemplateMode, processingRuleName, storeTiers, linkedPlanIds]);

  const handleUpdatePayoutRules = useCallback(async (): Promise<boolean> => {
    if (!processingRuleName || !storeTiers.length) return true;
    if (isPurseLockUpdateEnabled) {
      const tiers = storeTiers.map((rule) => {
        const isLocked = !!rule.isLockPurse;
        const planIds =
          isLocked && !isTemplateMode
            ? (rule.planIds || []).filter((planId) => linkedPlanIds?.includes(planId)).map(Number)
            : [];
        return {
          processing_tier_id: _.toNumber(rule.tierId),
          payout_rule_type: ProcessingRuleTierPayoutRuleType[rule.payoutRuleType],
          is_lock_purse: isLocked,
          plan_ids: planIds,
        };
      });

      const hasLockedTierWithoutPlans =
        !isTemplateMode && tiers.some((tier) => tier.is_lock_purse && !tier.plan_ids.length);
      if (hasLockedTierWithoutPlans) {
        setSaveValidate(true);
        setActivateValidate(true);
        return false;
      }

      await updateProcessingRuleTiers({
        processingRuleId: processingRuleName,
        payload: { tiers },
      });
      return true;
    }

    if (isTemplateMode) return true;

    await updatePayoutRules({
      processingRuleId: processingRuleName,
      payload: {
        tiers: storeTiers.map((rule) => ({
          processing_tier_id: Number(rule.tierId),
          payout_rule_type: rule.payoutRuleType,
        })),
      },
    });
    return true;
  }, [
    isTemplateMode,
    processingRuleName,
    storeTiers,
    isPurseLockUpdateEnabled,
    linkedPlanIds,
    updateProcessingRuleTiers,
    updatePayoutRules,
    setSaveValidate,
    setActivateValidate,
  ]);

  const handleUpdatePayoutRulesWithError = useCallback(async () => {
    try {
      const isValid = await handleUpdatePayoutRules();
      if (!isValid) {
        removeLoading();
        return false;
      }
    } catch {
      removeLoading();
      handleAddPermanentSnackbar({
        text: PAYOUT_RULES_ERROR_TEXT,
        state: SnackbarState.negative,
        closeIcon: true,
      });
      return false;
    }
    return true;
  }, [handleUpdatePayoutRules, removeLoading, handleAddPermanentSnackbar]);

  const handleShowError = useCallback(
    (error: unknown) => {
      removeLoading();
      const message = handleParseErors(error);
      if (message) {
        if (message.includes(ERROR_MESSAGES.PENDING_ORGANIZATION)) {
          setIsPendingOrganizationModal(true);
          return;
        }
      }
      const code = _.get(error, 'response.status', 0) as number;

      if (code >= ERRORS.SERVER_ERROR) {
        setSaveErrorModal(true);
      } else if (code === ERRORS.UNAUTHORIZED || !code) {
        return;
      } else {
        setIsEmptySaveModal(true);
      }
    },
    [handleParseErors, removeLoading, setIsEmptySaveModal, setIsPendingOrganizationModal, setSaveErrorModal]
  );
  const handleSavePlan = useUpdateHealthPlan({
    errorCallback: handleShowError,
    isTemplateMode,
  });

  const onSaveDraftHandler = useCallback(async () => {
    setSaveValidate(true);
    if (_.isEmpty(errorMessages)) {
      setLoader({ message: GlobalLoaderMessages.SAVE_PLAN_TO_DRAFT });

      const isPayoutRulesUpdated = await handleUpdatePayoutRulesWithError();
      if (!isPayoutRulesUpdated) return;

      await handleSavePlan(HealthPlanStatuses.DRAFT);
      handleAddPermanentSnackbar({
        text: `${planName} draft saved`,
        closeIcon: true,
        redirect: {
          path: ROUTES.HEALTH_PLANS,
          label: SNACKBAR_EXIT_TEXT,
          hash: isTemplateMode ? HealthPlansType.Template : '',
        },
      });
      removeLoading();
    } else if (_.isEmpty(successMessages) || !_.isEmpty(errorMessages)) {
      setIsEmptySaveModal(true);
    } else {
      setSaveErrorModal(true);
    }
  }, [
    setSaveValidate,
    errorMessages,
    isTemplateMode,
    successMessages,
    setLoader,
    handleSavePlan,
    removeLoading,
    handleAddPermanentSnackbar,
    planName,
    setSaveErrorModal,
    setIsEmptySaveModal,
    handleUpdatePayoutRulesWithError,
  ]);

  const onSubmitForReviewHandler = useCallback(async () => {
    setSaveValidate(true);
    if (_.isEmpty(errorMessages)) {
      setLoader({ message: GlobalLoaderMessages.SUBMIT_PLAN_TO_REVIEW });
      const isPayoutRulesUpdated = await handleUpdatePayoutRulesWithError();
      if (!isPayoutRulesUpdated) return;

      await handleSavePlan(HealthPlanStatuses.DRAFT);
      handleAddPermanentSnackbar({
        text: `${planName} submitted for review`,
        closeIcon: true,
        redirect: {
          path: ROUTES.HEALTH_PLANS,
          label: SNACKBAR_EXIT_TEXT,
          hash: isTemplateMode ? HealthPlansType.Template : '',
        },
      });
      removeLoading();
    } else if (_.isEmpty(successMessages) || !_.isEmpty(errorMessages)) {
      setIsEmptySubmitModal(true);
    } else {
      setSubmitErrorModal(true);
    }
  }, [
    setSaveValidate,
    errorMessages,
    isTemplateMode,
    successMessages,
    setLoader,
    handleSavePlan,
    removeLoading,
    handleAddPermanentSnackbar,
    planName,
    setSubmitErrorModal,
    setIsEmptySubmitModal,
    handleUpdatePayoutRulesWithError,
  ]);

  const onUpdateHandler = useCallback(() => {
    setActivateValidate(true);
    if (_.isEmpty(activateErrorMessages)) {
      setLoader({ message: GlobalLoaderMessages.UPDATE_ACTIVE_PLAN });
      // await handleSavePlan();
      removeLoading();
      handleAddPermanentSnackbar({
        text: `${planName} submitted for review`,
        closeIcon: true,
        redirect: {
          path: ROUTES.HEALTH_PLANS,
          label: SNACKBAR_EXIT_TEXT,
          hash: isTemplateMode ? HealthPlansType.Template : '',
        },
      });
    } else if (!_.isEmpty(activateErrorMessages)) {
      setIsEmptySaveModal(true);
    } else {
      setUpdateErrorModal(true);
    }
  }, [
    isTemplateMode,
    activateErrorMessages,
    handleAddPermanentSnackbar,
    planName,
    removeLoading,
    setActivateValidate,
    setIsEmptySaveModal,
    setLoader,
    setUpdateErrorModal,
  ]);

  const onActivateHandler = useCallback(async () => {
    setActivateValidate(true);
    if (_.isEmpty(activateErrorMessages)) {
      setLoader({ message: GlobalLoaderMessages.ACTIVATE_PLAN });
      const isPayoutRulesUpdated = await handleUpdatePayoutRulesWithError();
      if (!isPayoutRulesUpdated) return;

      await handleSavePlan(HealthPlanStatuses.ACTIVE);
      removeLoading();
      setSkipConfirmMode(true);
      handleAddPermanentSnackbar({
        text: `${planName} activated!`,
        closeIcon: true,
        redirect: {
          path: ROUTES.HEALTH_PLANS,
          label: SNACKBAR_EXIT_TEXT,
          hash: isTemplateMode ? HealthPlansType.Template : '',
        },
      });
      setEditMode(false);
    } else if (!_.isEmpty(activateErrorMessages)) {
      setActivateModal(true);
    } else {
      setActivateErrorModal(true);
    }
  }, [
    isTemplateMode,
    setActivateValidate,
    activateErrorMessages,
    setLoader,
    handleSavePlan,
    removeLoading,
    setSkipConfirmMode,
    handleAddPermanentSnackbar,
    planName,
    setActivateErrorModal,
    setActivateModal,
    setEditMode,
    handleUpdatePayoutRulesWithError,
  ]);

  const updateActiveHP = useCallback(async () => {
    setLoader({ message: GlobalLoaderMessages.ACTIVATE_PLAN });
    const isPayoutRulesUpdated = await handleUpdatePayoutRulesWithError();
    if (!isPayoutRulesUpdated) return;

    await handleSavePlan(HealthPlanStatuses.ACTIVE);
    removeLoading();
    setSkipConfirmMode(true);
    handleAddPermanentSnackbar({
      text: `${planName} updated!`,
      closeIcon: true,
      redirect: {
        path: ROUTES.HEALTH_PLANS,
        label: SNACKBAR_EXIT_TEXT,
        hash: isTemplateMode ? HealthPlansType.Template : '',
      },
    });
  }, [
    isTemplateMode,
    setLoader,
    handleSavePlan,
    removeLoading,
    setSkipConfirmMode,
    handleAddPermanentSnackbar,
    planName,
    handleUpdatePayoutRulesWithError,
  ]);

  const onUpdateActiveHPHandler = useCallback(
    async (action: () => void) => {
      setActivateValidate(true);
      if (hasLockedTierWithoutPlans) {
        setIsEmptySaveModal(true);
        return;
      }
      if (_.isEmpty(activateErrorMessages)) {
        action();
      } else if (!_.isEmpty(activateErrorMessages)) {
        setIsEmptySaveModal(true);
      } else {
        setActivateErrorModal(true);
      }
    },
    [
      setActivateValidate,
      hasLockedTierWithoutPlans,
      activateErrorMessages,
      setActivateErrorModal,
      setIsEmptySaveModal,
    ]
  );

  return {
    onSaveDraftHandler,
    onSubmitForReviewHandler,
    onUpdateHandler,
    onActivateHandler,
    errorMessages,
    successMessages,
    pendingMessages,
    activateErrorMessages,
    activateSuccessMessages,
    activatePedingMessages,
    updateActiveHP,
    onUpdateActiveHPHandler,
  };
};
